/**
 * Typed API client for the FastAPI backend.
 *
 * Token strategy mirrors the backend design (Section 3.2): the short-lived access token is
 * held in memory only, and the long-lived refresh token is persisted so a reload keeps the
 * session. A single-flight refresh serialises concurrent 401s into one rotation.
 */
import { env } from "./env";
import type {
  AdminCommunityItem,
  AdminSystemStats,
  AdminUserItem,
  Comment,
  CommentPage,
  Community,
  CommunityAccessType,
  CommunityJoinRequest,
  CommunityMember,
  FeedSort,
  LikeResponse,
  ModerationQueueItem,
  ModerationStats,
  Page,
  ReportReason,
  ReportResponse,
  SearchParams,
  SessionInfo,
  TokenPair,
  UploadUrlResponse,
  UserPrivate,
  UserPublic,
  VideoAudience,
  VideoDetail,
  VideoOut,
  VideoStatusResponse,
  ViewResponse,
} from "./types";

export const API_BASE: string = env.apiBase;
const REFRESH_STORAGE_KEY = "reelai.refresh";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: { field: string; message: string }[];
  readonly retryAfter: number | null;

  constructor(
    status: number,
    code: string,
    message: string,
    fields: { field: string; message: string }[] = [],
    retryAfter: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.retryAfter = retryAfter;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
}

/* ------------------------------------------------------------------ tokens */

let accessToken: string | null = null;
let refreshToken: string | null = readStoredRefresh();
const listeners = new Set<(hasSession: boolean) => void>();

function readStoredRefresh(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null; // Private mode or storage disabled: the session lasts until reload.
  }
}

export function setTokens(pair: TokenPair | null): void {
  accessToken = pair?.access_token ?? null;
  refreshToken = pair?.refresh_token ?? null;
  try {
    if (refreshToken) window.localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
    else window.localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  for (const listener of listeners) listener(Boolean(refreshToken));
}

export function onSessionChange(fn: (hasSession: boolean) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export const hasStoredSession = (): boolean => Boolean(refreshToken);
export const currentRefreshToken = (): string | null => refreshToken;

/* ----------------------------------------------------------------- request */

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
  /** Internal guard against an infinite refresh loop. */
  retried?: boolean;
}

let refreshInFlight: Promise<boolean> | null = null;

async function rotateRefreshToken(): Promise<boolean> {
  if (!refreshToken) return false;
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(API_BASE + "/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        setTokens(null);
        return false;
      }
      setTokens((await res.json()) as TokenPair);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = "http_" + res.status;
  let message = res.statusText || "Request failed";
  let fields: { field: string; message: string }[] = [];
  try {
    const payload = await res.json();
    const error = payload?.error;
    if (error) {
      code = error.code ?? code;
      message = error.message ?? message;
      fields = error.details?.fields ?? [];
    } else if (typeof payload?.detail === "string") {
      message = payload.detail;
    }
  } catch {
    /* Non-JSON body: proxy error page, 502, and so on. */
  }
  const retryAfter = Number(res.headers.get("Retry-After"));
  return new ApiError(
    res.status,
    code,
    message,
    fields,
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
  );
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true, signal } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth && accessToken) headers.Authorization = "Bearer " + accessToken;

  const res = await fetch(API_BASE + path, {
    method,
    headers,
    // The anonymous view-dedup cookie (avs) rides along on view reporting.
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 401 && auth && !options.retried && refreshToken) {
    if (await rotateRefreshToken()) {
      return request<T>(path, { ...options, retried: true });
    }
  }
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Ensures a usable access token exists. Used on boot and before an upload. */
export async function ensureAccessToken(): Promise<boolean> {
  if (accessToken) return true;
  return rotateRefreshToken();
}

function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? "?" + encoded : "";
}

/* --------------------------------------------------------------- endpoints */

export const api = {
  auth: {
    register: (body: { username: string; email: string; password: string }) =>
      request<UserPrivate>("/auth/register", { method: "POST", body, auth: false }),
    login: (body: { identifier: string; password: string }) =>
      request<TokenPair>("/auth/login", { method: "POST", body, auth: false }),
    google: (body: { credential: string }) =>
      request<TokenPair>("/auth/google", { method: "POST", body, auth: false }),
    telegramLogin: () =>
      request<{ url: string }>("/auth/telegram/login", { auth: false }),
    exchange: (body: { code: string }) =>
      request<TokenPair>("/auth/exchange", { method: "POST", body, auth: false }),
    logout: (body: { refresh_token?: string | null; all_sessions?: boolean }) =>
      request<{ message: string }>("/auth/logout", { method: "POST", body }),
    me: () => request<UserPrivate>("/auth/me"),
    sessions: () => request<SessionInfo[]>("/auth/sessions"),
  },
  users: {
    profile: (username: string) => request<UserPublic>("/users/" + encodeURIComponent(username)),
    updateMe: (body: { bio?: string | null; avatar_url?: string | null }) =>
      request<UserPrivate>("/users/me", { method: "PATCH", body }),
  },
  communities: {
    list: (
      params?: { q?: string | null; member?: string | null; owner?: string | null } | string | null,
      signal?: AbortSignal,
    ) => {
      const queryObj = typeof params === "string" ? { q: params } : params || {};
      return request<Community[]>("/communities" + query(queryObj), { auth: hasStoredSession(), signal });
    },
    create: (body: {
      name: string;
      description?: string | null;
      access_type?: CommunityAccessType;
      initial_members?: string[];
    }) => request<Community>("/communities", { method: "POST", body }),
    get: (key: string, signal?: AbortSignal) =>
      request<Community>("/communities/" + encodeURIComponent(key), { auth: hasStoredSession(), signal }),
    requestJoin: (key: string) =>
      request<CommunityJoinRequest>("/communities/" + encodeURIComponent(key) + "/join-requests", {
        method: "POST",
      }),
    listJoinRequests: (key: string, status?: string, signal?: AbortSignal) =>
      request<CommunityJoinRequest[]>(
        "/communities/" + encodeURIComponent(key) + "/join-requests" + query({ status }),
        { signal },
      ),
    reviewJoinRequest: (key: string, requestId: string, action: "approve" | "reject") =>
      request<CommunityJoinRequest>(
        "/communities/" +
          encodeURIComponent(key) +
          "/join-requests/" +
          encodeURIComponent(requestId) +
          "/review",
        { method: "POST", body: { action } },
      ),
    listMembers: (key: string, signal?: AbortSignal) =>
      request<CommunityMember[]>("/communities/" + encodeURIComponent(key) + "/members", {
        auth: hasStoredSession(),
        signal,
      }),
    addMember: (key: string, username: string) =>
      request<CommunityMember>("/communities/" + encodeURIComponent(key) + "/members", {
        method: "POST",
        body: { username },
      }),
    removeMember: (key: string, userId: string) =>
      request<void>(
        "/communities/" + encodeURIComponent(key) + "/members/" + encodeURIComponent(userId),
        { method: "DELETE" },
      ),
  },
  videos: {
    feed: (
      params: {
        sort?: FeedSort;
        cursor?: string | null;
        limit?: number;
        author?: string | null;
        community?: string | null;
      },
      signal?: AbortSignal,
    ) => request<Page<VideoOut>>("/videos" + query(params), { auth: hasStoredSession(), signal }),
    search: (params: SearchParams, signal?: AbortSignal) =>
      request<Page<VideoOut>>(
        "/videos/search" +
          query(params as unknown as Record<string, string | number | boolean | null | undefined>),
        { auth: hasStoredSession(), signal },
      ),
    mine: (params: { cursor?: string | null; limit?: number }, signal?: AbortSignal) =>
      request<Page<VideoOut>>("/videos/mine" + query(params), { signal }),
    detail: (id: string, signal?: AbortSignal) =>
      request<VideoDetail>("/videos/" + id, { auth: hasStoredSession(), signal }),
    uploadUrl: (body: {
      filename: string;
      content_type: string;
      file_size: number;
      community: string;
    }) => request<UploadUrlResponse>("/videos/upload-url", { method: "POST", body }),
    confirm: (body: {
      video_id: string;
      title: string;
      description?: string | null;
      publish?: boolean;
      community?: string | null;
      audience?: VideoAudience;
      allowed_usernames?: string[];
    }) => request<VideoStatusResponse>("/videos", { method: "POST", body }),
    update: (
      id: string,
      body: {
        title?: string;
        description?: string | null;
        publish?: boolean;
        community?: string | null;
        audience?: VideoAudience;
        allowed_usernames?: string[];
      },
    ) => request<VideoDetail>("/videos/" + id, { method: "PATCH", body }),
    remove: (id: string) => request<void>("/videos/" + id, { method: "DELETE" }),
    like: (id: string) => request<LikeResponse>("/videos/" + id + "/like", { method: "POST" }),
    unlike: (id: string) => request<LikeResponse>("/videos/" + id + "/like", { method: "DELETE" }),
    view: (id: string, watched_seconds: number) =>
      request<ViewResponse>("/videos/" + id + "/view", {
        method: "POST",
        body: { watched_seconds },
        auth: hasStoredSession(),
      }),
    report: (id: string, body: { reason: ReportReason; detail?: string | null }) =>
      request<ReportResponse>("/videos/" + id + "/report", { method: "POST", body }),
    comments: (
      id: string,
      params?: { cursor?: string | null; limit?: number },
      signal?: AbortSignal,
    ) =>
      request<CommentPage>("/videos/" + id + "/comments" + query(params ?? {}), {
        auth: hasStoredSession(),
        signal,
      }),
    addComment: (id: string, content: string) =>
      request<Comment>("/videos/" + id + "/comments", { method: "POST", body: { content } }),
    deleteComment: (id: string, commentId: string) =>
      request<{ message: string }>("/videos/" + id + "/comments/" + commentId, {
        method: "DELETE",
      }),
  },
  moderation: {
    queue: (params: { cursor?: string | null; limit?: number }) =>
      request<Page<ModerationQueueItem>>("/moderation/queue" + query(params)),
    review: (
      videoId: string,
      body: { action: "approve" | "reject"; reason?: string | null; notes?: string | null },
    ) =>
      request<{ video_id: string; status: string; decision: string }>(
        "/moderation/" + videoId + "/review",
        { method: "POST", body },
      ),
    stats: () => request<ModerationStats>("/moderation/stats"),
  },
  admin: {
    stats: () => request<AdminSystemStats>("/admin/stats"),
    users: (params?: { q?: string | null }) =>
      request<AdminUserItem[]>("/admin/users" + query(params ?? {})),
    updateUserStatus: (userId: string, is_active: boolean) =>
      request<AdminUserItem>("/admin/users/" + userId + "/status", {
        method: "PATCH",
        body: { is_active },
      }),
    updateUserRole: (userId: string, is_admin: boolean) =>
      request<AdminUserItem>("/admin/users/" + userId + "/role", {
        method: "PATCH",
        body: { is_admin },
      }),
    communities: (params?: { q?: string | null }) =>
      request<AdminCommunityItem[]>("/admin/communities" + query(params ?? {})),
    deleteCommunity: (communityId: string) =>
      request<void>("/admin/communities/" + communityId, { method: "DELETE" }),
  },
};

/**
 * Uploads the bytes straight to object storage with progress, exactly as the backend
 * intends: the API never sees the file. XHR rather than fetch, for upload progress events.
 */
export function putToStorage(
  target: UploadUrlResponse,
  file: File,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(target.method || "PUT", target.upload_url, true);
    const headers = target.headers ?? {};
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);
    if (!headers["Content-Type"] && !headers["content-type"]) {
      xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        reject(
          new ApiError(
            xhr.status,
            "storage_upload_failed",
            xhr.responseText || "Upload rejected by storage.",
          ),
        );
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "network_error", "Network error while uploading."));
    xhr.onabort = () => reject(new ApiError(0, "aborted", "Upload cancelled."));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}
